import { TranslateService } from '@ngx-translate/core';
import { Locale, LocaleTimeUtils } from '../../shared/utils/locale-time.utils';

function formatTooltipValue(value: number, locale: string, maximumFractionDigits = 1): string {
    return new Intl.NumberFormat(LocaleTimeUtils.normalize(locale), { maximumFractionDigits }).format(value);
}

export function customTooltip(context: any, translate: TranslateService, locale: string, formatDate: (date: Date) => string) {
    const showProduct = LocaleTimeUtils.matchesLocale(locale, Locale.ES_ES);
    const tooltipModel = context.tooltip;
    let tooltipEl = document.getElementById("chartjs-tooltip");

    if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = "chartjs-tooltip";
        Object.assign(tooltipEl.style, {
            position: "absolute",
            background: "#FFFF",
            borderRadius: "1rem",
            pointerEvents: "none",
            minWidth: "18.75rem",
            maxWidth: "26rem",
            width: "max-content",
            boxSizing: "border-box",
            padding: "1rem",
            transition: "all 0.1s ease",
            zIndex: "95",
            boxShadow: "0 0 16px 0 rgba(102, 119, 144, 0.20)",
            fontFamily: "'Roobert ENEL', sans-serif",
        });
        document.body.appendChild(tooltipEl);
    }


    const { chart, tooltip } = context;

    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = '0';
        return;
    }

    // every series in the date column is reported, and the padding points of series with no
    // event that day are filtered out, which can leave nothing to show
    const dataPoints = tooltipModel.dataPoints;
    if (!dataPoints.length) {
        tooltipEl.style.opacity = '0';
        return;
    }

    tooltipEl.style.opacity = '1';
    tooltipEl.style.position = 'absolute';

    const barElementFor = (item: any) => chart.getDatasetMeta(item.datasetIndex).data[item.dataIndex];

    // when several series share a date, show the bar the cursor is nearest to
    const dataPoint = dataPoints.reduce((closest: any, candidate: any) =>
        Math.abs(barElementFor(candidate).x - tooltipModel.caretX) < Math.abs(barElementFor(closest).x - tooltipModel.caretX) ? candidate : closest
    );
    const raw = dataPoint.raw;
    const dataset = dataPoint.dataset;

    // Calculate Percentage for the bottom row

    const headerHtml = `
    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem;">
        <span style="font-weight: 700; font-size: 0.75rem; color: #272C34; margin-right: 0.375rem;">${dataset.label}</span>
        <span style="font-size: 0.75rem; color: #667790;">${formatDate(raw.eventDate)}</span>
    </div>
  `;

    const t = (key: string) => translate.instant(key);

    const productRowHtml = showProduct
      ? `<div><span style="color: #667790;">${t('PD.LABELS.PRODUCT')}:</span> ${raw.productName || '-'}</div>`
      : '';

    const metaHtml = `
    <div style="margin-bottom: 0.75rem; font-size: 0.75rem; color: #272C34; gap:0.375rem; display: flex; flex-direction: column; line-height: 1rem;">
        <div><span>${t('PD.LABELS.PROGRAM')}:</span> ${raw.programName || '-'}</div>
        ${productRowHtml}
        <div><span>${t('PD.LABELS.PRODUCT')}:</span> ${raw.productName || '-'}</div>
       <!-- <div><span style="color: #667790;">${t('PD.LABELS.ORGANIZATION')}:</span> ${raw.organizationName || '-'}</div> -->
    </div>
  `;

    const enrollmentHtml = `
    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem;">
        <div style="display: flex; align-items: center; gap: 0.25rem; height: 1rem;">
            <div style="display: flex; align-items: center; width:1rem; height:1rem; justify-content: center;">
                <div style="width: 0.815rem; height: 0.1875rem; background: #000000;"></div>
            </div>
            <span style="font-size: 0.75rem; color: #272C34;">${t('PD.LABELS.ENROLLMENT')}</span>
        </div>
        <span style="font-size: 0.75rem; font-weight: 700; color: #272C34; line-height: 1rem;">${formatTooltipValue(raw.enrollment, locale)} kW</span>
    </div>
  `;

    const performanceHtml = `
    <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;">
        <div style="display: flex; align-items: center; gap: 0.25rem; height: 1rem; flex-shrink: 0;">
            <div style="display: flex; align-items: center; width:1rem; height:1rem; justify-content: center;">
                <div style="width: 0.625rem; height: 0.625rem; background: ${dataset.backgroundColor}; border-radius: 50%;"></div>
            </div>
            <span style="font-size: 0.75rem; color: #272C34;">${t('PD.LABELS.PERFORMANCE_KW')}</span>
        </div>
        <div style="font-size: 0.75rem; color: #272C34; line-height: 1rem; text-align: right; white-space: nowrap;">
            <span style="font-weight: 700;">${formatTooltipValue(raw.y, locale)} kW</span>
            <span> ${t('PD.LABELS.OF')} ${formatTooltipValue(raw.enrollment, locale)} kW</span>
            <span style="font-weight: 700;">(${formatTooltipValue(raw.performance, locale, 0)}%)</span>
        </div>
    </div>
  `;

    // Combine them
    tooltipEl.innerHTML = headerHtml + metaHtml + enrollmentHtml + performanceHtml;

    // Caret pointing at the bar. Absolutely positioned, so it does not affect the width
    // measured below.
    const caret = document.createElement('div');
    Object.assign(caret.style, {
        position: 'absolute',
        width: '0',
        height: '0',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
    });
    tooltipEl.appendChild(caret);

    // Positioning of the tooltip

    const canvasRect = chart.canvas.getBoundingClientRect();
    const { top: chartTop, bottom: chartBottom } = chart.chartArea;
    const midY = (chartTop + chartBottom) / 2;
    const offset = 30;

    // Anchor to the end of the bar instead of the cursor, so the tooltip holds still while
    // the pointer moves around inside the same date column
    const bar = barElementFor(dataPoint);

    let top = 0;
    const isBelowBar = bar.y < midY;

    if (isBelowBar) {
        // BELOW the bar
        top = canvasRect.top + window.scrollY + bar.y + offset;
    } else {
        //  ABOVE the bar
        top = canvasRect.top + window.scrollY + bar.y - tooltipEl.offsetHeight - offset;
    }

    // Center horizontally
    let left = canvasRect.left + window.scrollX + bar.x - (tooltipEl.offsetWidth / 2);

    // Keep it on screen: at the edges of the chart it would otherwise spill outside the
    // document, adding a scrollbar that reflows the page while hovering
    const margin = 8;
    left = Math.max(window.scrollX + margin, Math.min(left, window.scrollX + document.documentElement.clientWidth - tooltipEl.offsetWidth - margin));
    top = Math.max(window.scrollY + margin, Math.min(top, window.scrollY + document.documentElement.clientHeight - tooltipEl.offsetHeight - margin));

    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.left = `${left}px`;

    // Point the caret at the bar, which stays correct after the clamping above has moved the
    // tooltip sideways. It overlaps the edge by a pixel because the tooltip sits at a
    // fractional offset, and rounding its edge and the caret's in opposite directions would
    // otherwise leave a hairline gap between them.
    if (isBelowBar) {
        caret.style.top = '-7px';
        caret.style.borderBottom = '8px solid #FFFFFF';
    } else {
        caret.style.bottom = '-7px';
        caret.style.borderTop = '8px solid #FFFFFF';
    }
    const barPageX = canvasRect.left + window.scrollX + bar.x;
    const caretInset = 16;
    caret.style.left = `${Math.min(Math.max(barPageX - left - 8, caretInset), tooltipEl.offsetWidth - caretInset - 16)}px`;
}


export const yScalesCallback = (value: number) => {
    return Number(value.toFixed(1));
    
};

export const yTitleTopPlugin = {
    id: 'yTitleTop',
    beforeLayout: (chart: any, args: any, opts: any) => {
        const {
            display,
            font
        } = opts;
        if (!display) {
            return;
        }

        const {
            ctx
        } = chart;
        ctx.font = font;

        const {
            width
        } = ctx.measureText(opts.text);
        chart.options.layout.padding.left = width * 1.1;
    },
    afterDraw: (chart: any, args: any, opts: any) => {
        const { text } = opts;
        const { ctx } = chart;

        if (opts.display) {
            ctx.font = "800 0.75rem 'Roobert ENEL', sans-serif";
            ctx.fillStyle = '#000';
            ctx.fillText(text, 0, 14);
        }

    }
};


export const verticalSeparatorPlugin = {
    id: 'verticalSeparatorPlugin',
    beforeDatasetsDraw: (chart: any, args: any, options: any) => {
        const { ctx, chartArea: { top, bottom } } = chart;

        ctx.save();
        ctx.lineWidth = 1; 
        ctx.strokeStyle = '#eff2f7'; // Color of the line

        chart.data.datasets.forEach((dataset: any, datasetIndex: any) => {

            const meta = chart.getDatasetMeta(datasetIndex);

            // Skip this dataset if it is hidden
            if (!meta.visible) return;

            // Iterate over every bar inside this dataset
            meta.data.forEach((bar: any) => {

                const xPos = bar.x;

                // Draw the line
                ctx.beginPath();
                ctx.moveTo(xPos, top);
                ctx.lineTo(xPos, bottom);
                ctx.stroke();
            });
        });
    }
};

export const enrollmentLinePlugin = {
    id: 'enrollmentLine',
    afterDatasetsDraw(chart: any, args: any, options: any) {
        const { ctx, scales: { y } } = chart;

        ctx.save();
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;

        chart.data.datasets.forEach((dataset: any, i: number) => {

            // Skip this dataset if it is hidden
            if (!chart.isDatasetVisible(i)) return;
            const meta = chart.getDatasetMeta(i);

            meta.data.forEach((bar: any, index: number) => {
                // Data object associated with this bar
                const rawData = dataset.data[index];

                if (!rawData || typeof rawData.enrollment !== 'number') return;
                if (bar.skip || bar.hidden) return;

                const enrollmentVal = rawData.enrollment;

                // Calculate Position
                const yPixel = y.getPixelForValue(enrollmentVal);
                const xPos = bar.x;
                const barWidth = bar.width;

                // Draw Line, exactly as wide as its bar so neighbouring markers keep the
                // same gap the bars have and never merge into one continuous line
                ctx.beginPath();
                ctx.moveTo(xPos - (barWidth / 2), yPixel);
                ctx.lineTo(xPos + (barWidth / 2), yPixel);
                ctx.stroke();
            });
        });

        ctx.restore();
    }
};
